// FloatingMenuComponent.jsx
// React component for the floating menu UI

import React from "react";
import {Button, ButtonGroup, CircularProgress, Paper} from "@mui/material";
import {getSelectionButtonConfigs, LINK_BUTTON_CONFIGS} from "./floatingMenuConfigs";
import UserMentionInfoCard from "../../components/UserMentionInfoCard";
import HashtagInfoCard from "../../components/HashtagInfoCard";

/**
 * FloatingMenu React Component using MUI
 * @param {Function} onButtonClick - Callback when a button is clicked
 * @param {string} menuType - Type of menu to display ("selection", "link", or "nodeInfo")
 * @param {Object|null} selectedNodeInfo - Mention/hashtag data for nodeInfo mode
 * @param {string} loadingButton - Type of button currently in loading state
 * @param {Function|null} onNodeInfoClose - Callback used by node info actions (e.g. Send Message)
 */
export function FloatingMenuComponent({onButtonClick, menuType, selectedNodeInfo, loadingButton, onNodeInfoClose}) {
    if (menuType === "nodeInfo" && selectedNodeInfo) {
        const isMention = selectedNodeInfo.type === "userMention";

        return (
            <Paper
                elevation={3}
                sx={{
                    borderRadius: 1,
                    whiteSpace: "normal",
                    maxWidth: 360,
                    overflow: "hidden"
                }}
            >
                {isMention ? (
                    <UserMentionInfoCard
                        userData={{
                            id: selectedNodeInfo.id,
                            uid: selectedNodeInfo.uid,
                            label: selectedNodeInfo.label,
                            name: selectedNodeInfo.name,
                            email: selectedNodeInfo.email,
                            position: selectedNodeInfo.position,
                            headshot: selectedNodeInfo.headshot,
                        }}
                        onMessageOpened={onNodeInfoClose || undefined}
                        sx={{p: 1.5, minWidth: 300, maxWidth: 360}}
                    />
                ) : (
                    <HashtagInfoCard
                        tagData={{
                            id: selectedNodeInfo.id,
                            label: selectedNodeInfo.label,
                            docId: selectedNodeInfo.docId,
                        }}
                        sx={{p: 1.5, minWidth: 280, maxWidth: 340}}
                    />
                )}
            </Paper>
        );
    }

    const configs = menuType === "link"
        ? LINK_BUTTON_CONFIGS
        : getSelectionButtonConfigs();

    return (
        <Paper
            elevation={3}
            sx={{
                display: 'inline-flex',
                borderRadius: 1,
                overflow: 'hidden',
                width: 'max-content',
                maxWidth: 'none',
                whiteSpace: 'nowrap'
            }}
        >
            <ButtonGroup
                variant="text"
                size="small"
                color="inherit"
                sx={{
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap'
                }}
            >
                {configs.map(config => {
                    const IconComponent = config.icon;
                    const isLoading = loadingButton === config.type;
                    return (
                        <Button
                            key={config.type}
                            onClick={() => onButtonClick(config.type)}
                            startIcon={isLoading ? <CircularProgress size={16}/> : <IconComponent/>}
                            title={config.title}
                            disabled={isLoading}
                            sx={{
                                flexShrink: 0,
                                minWidth: 'auto',
                                whiteSpace: 'nowrap',
                                textTransform: 'none',
                                px: 1.5,
                                py: 0.75,
                                fontSize: '13px',
                                fontWeight: 500,
                                color: 'text.primary'
                            }}
                        >
                            {config.label}
                        </Button>
                    );
                })}
            </ButtonGroup>
        </Paper>
    );
}
